import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cognitoUserManagementService } from '@/services/cognitoUserManagement.service';
import type { CognitoUser, CreateUserInput } from '@/services/cognitoUserManagement.service';
import type { SortField, SortOrder } from '@/components/UserAccess/types';
import { USER_ACCESS_PAGINATION } from '@/config/constant';
import type { UserRole } from '@/config/constant';
import { isValidEmail, exportUsersToCSV } from '@/utils/userAccess.utils';

export const useUserAccess = () => {
  const navigate = useNavigate();

  // Core Data State
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<CognitoUser[]>([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Search & Pagination State
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(USER_ACCESS_PAGINATION.DEFAULT_ROWS_PER_PAGE);

  // Sorting State
  const [sortField, setSortField] = useState<SortField>('email');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Form State
  const [newUser, setNewUser] = useState<CreateUserInput>({
    email: '',
    role: 'user',
  });

  // ─── Core Data Operations ───────────────────────────────────────────────────

  /**
   * Load all users from Cognito. Wrapped in useCallback so it is a stable
   * reference and can be listed as a dependency in useEffect safely.
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userList = await cognitoUserManagementService.listUsers();
      setUsers(userList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Filter users based on the current search query.
   * Always resets pagination to page 0 to prevent empty table when users list changes.
   */
  const filterUsers = useCallback(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      setPage(0); // Reset page even when clearing search
      return;
    }
    const query = searchQuery.toLowerCase();
    setFilteredUsers(users.filter((u) => u.email.toLowerCase().includes(query)));
    setPage(0);
  }, [searchQuery, users]);

  /**
   * Return a sorted copy of the provided list without mutating the original.
   */
  const sortUsers = useCallback(
    (usersToSort: CognitoUser[]) =>
      [...usersToSort].sort((a, b) => {
        const aValue = a[sortField].toLowerCase();
        const bValue = b[sortField].toLowerCase();
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }),
    [sortField, sortOrder],
  );

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  // ─── Action Handlers ────────────────────────────────────────────────────────

  /** Submit the Add User form */
  const handleAddUser = async () => {
    if (!newUser.email) {
      setError('Email is required');
      return;
    }
    if (!isValidEmail(newUser.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await cognitoUserManagementService.createUser(newUser);
      setSuccess(
        `User ${newUser.email} created successfully. A temporary password has been sent to their email.`,
      );
      setNewUser({ email: '', role: 'user' });
      setShowAddModal(false);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  /** Open the delete confirmation dialog for the given email */
  const handleDeleteClick = (email: string) => {
    setUserToDelete(email);
    setShowDeleteModal(true);
  };

  /** Confirm and execute user deletion */
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await cognitoUserManagementService.deleteUser(userToDelete);
      setSuccess(`User ${userToDelete} deleted successfully`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // ─── Search & Pagination Handlers ───────────────────────────────────────────

  const handleSearchChange = (value: string) => setSearchQuery(value);

  const handleSearchReset = () => {
    setSearchQuery('');
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // ─── Sort Handler ────────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // ─── Modal Handlers ──────────────────────────────────────────────────────────

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    setError(null);
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setNewUser({ email: '', role: 'user' });
    setError(null);
  };

  // ─── Form Field Handlers ─────────────────────────────────────────────────────

  const handleRoleChange = (role: UserRole) =>
    setNewUser((prev) => ({ ...prev, role }));

  const handleEmailChange = (email: string) =>
    setNewUser((prev) => ({ ...prev, email }));

  // ─── Export ──────────────────────────────────────────────────────────────────

  const handleExportUsers = () => {
    exportUsersToCSV(filteredUsers);
  };

  // ─── Navigation ──────────────────────────────────────────────────────────────

  const handleBackToDashboard = () => navigate('/');

  // ─── Alert Dismissal (prefer these over exposing raw setState) ──────────────

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const sortedUsers = sortUsers(filteredUsers);
  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return {
    // Data
    users,
    filteredUsers,
    paginatedUsers,

    // UI state
    loading,
    error,
    success,

    // Modal state
    showAddModal,
    showDeleteModal,
    userToDelete,

    // Search & Pagination
    searchQuery,
    page,
    rowsPerPage,

    // Sorting
    sortField,
    sortOrder,

    // Form
    newUser,

    // Handlers
    handleAddUser,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSearchReset,
    handleExportUsers,
    handleBackToDashboard,
    handleRoleChange,
    handleEmailChange,
    handleSearchChange,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage,
    handleAddModalOpen,
    handleAddModalClose,

    // Alert dismissal
    clearError,
    clearSuccess,
  };
};